// Per-team HSA/FSA enablement slice of the storefront autocheck cache.
// Discriminated on `enabled` so future fields (e.g. variant whitelist,
// PDP placement override) can be added without touching disabled-merchant
// code paths — matches the `ConversionAutocheck` pattern.
//
// Source: `team.settings.hsa?.enabled` on the Mongo Team document.
// Produced by `getHsaAutocheckForTeam` in
// `redo/conversion/hsa/service/src/storefront.ts`. Consumed by the
// storefront extension (`redo/shopify/extension/src/`) to gate the
// questionnaire bundle init.
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";

// Where the storefront extension injects the "Pay with HSA/FSA" PDP
// eligibility widget (mirrors `team.settings.hsa.pdpWidgetPlacement`).
export interface HsaPdpWidgetPlacement {
  cssSelector: string;
  insertionPosition?: InsertionPosition;
}

// One placement of the HSA/FSA cart eligibility widget on the storefront cart
// (cart page or slide-in drawer). Mirrors one entry of
// `team.settings.hsa.cartWidgetPlacements`.
export interface HsaCartWidgetPlacement {
  selector: string;
  insertPosition: InsertionPosition;
}

// Which storefront element interactions record an HSA intent signal, floating
// the HSA/FSA payment method at checkout for that cart. Mirrors
// `team.settings.hsa.intentSignals` with product defaults applied. The buy
// button is not governed here: it always records, and `buyButtons` governs
// its exclusivity.
export interface HsaIntentSignalSettings {
  lmnStarted: boolean;
  lmnCompleted: boolean;
  pdpWidget: boolean;
  cartWidget: boolean;
}

// Product default: every element records. Absent stored values resolve here so
// a merchant who never touched the switches gets the full behavior.
export function resolveHsaIntentSignalSettings(
  stored:
    | { [K in keyof HsaIntentSignalSettings]?: boolean | null }
    | null
    | undefined,
): HsaIntentSignalSettings {
  return {
    lmnStarted: stored?.lmnStarted ?? true,
    lmnCompleted: stored?.lmnCompleted ?? true,
    pdpWidget: stored?.pdpWidget ?? true,
    cartWidget: stored?.cartWidget ?? true,
  };
}

export type HsaAutocheck =
  | { enabled: false }
  | {
      enabled: true;
      // When set, the storefront extension appends `skip_shop_pay=true` to
      // every entry into Shopify checkout so signed-in Shop Pay customers
      // land on the standard checkout (where the HSA/FSA payment method is
      // reachable) instead of Shop Pay's accelerated checkout.
      checkoutButtonsSkipShopPay: boolean;
      // Merchant settings scoped to the `window.RedoHsa` entry points only.
      buyButtons: {
        // When true (merchant default), those checkout entries write the
        // per-cart exclusivity attribute so only HSA/FSA shows at checkout.
        showOnlyHsaPaymentMethod: boolean;
      };
      // Optional on the wire: the storefront caches this payload in the
      // shopper's browser, so a copy saved before the switches existed can
      // arrive without it. Read it through `resolveHsaIntentSignalSettings`.
      intentSignals?: HsaIntentSignalSettings;
      // Absent = merchant hasn't configured a placement, so the storefront
      // extension does not render the PDP widget.
      pdpWidgetPlacement?: HsaPdpWidgetPlacement;
      // Absent/empty = merchant hasn't configured any cart placements, so the
      // storefront extension does not render the cart eligibility widget.
      cartWidgetPlacements?: HsaCartWidgetPlacement[];
    };
